/**
 * Fees — gestion de cuotas de la asociacion.
 *
 * Conectada a /api/management (F4). Cuatro tabs:
 *   - Cobros        Lista MemberFeeStatus con cambio rapido de estado
 *                   (pending ↔ paid ↔ exempt). Al marcar paid se ve reflejado
 *                   en Presupuesto sin tocar nada manualmente.
 *   - Planes        CRUD de MemberFee. Cada plan puede enlazar opcionalmente
 *                   con una BudgetLine; cuando un cobro pasa a paid el backend
 *                   materializa automaticamente un BudgetEntry en esa partida.
 *   - Asignacion    Bulk-assign: aplica un plan a una seleccion de socios para
 *                   un periodo en una sola operacion.
 *   - Campanias     CRUD basico de FeeCampaign (sin envio email todavia).
 *
 * Las cantidades se guardan en CENTIMOS; la UI las muestra en EUROS.
 *
 * Decisiones en DOC/ES/MODULO_CUOTAS_DECISIONES.md.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  XMarkIcon,
  CheckCircleIcon,
  ClockIcon,
  MinusCircleIcon,
  PaperAirplaneIcon,
  NoSymbolIcon,
  ArrowRightIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import { Header } from '@/components/Layout';
import {
  feesApi,
  membersApi,
  budgetsApi,
  type MemberFee,
  type MemberFeeStatusRow,
  type MemberFeeStatusValue,
  type FeeCampaign,
  type FeePeriod,
  type Member,
  type BudgetLine,
} from '@/services/api';
import { useManagementFeature } from '@/hooks/useManagementFeature';
import { useAuthStore } from '@/stores/auth';
import { PERMISSIONS } from '@/types';
import toast from 'react-hot-toast';

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

function formatEur2(centimos: number): string {
  return (centimos / 100).toLocaleString('es-ES', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 2,
  });
}

function csvCell(v: unknown): string {
  const s = v == null ? '' : String(v);
  if (s === '') return '';
  if (/[",\n\r;]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function downloadCsv(filename: string, rows: string[][]): void {
  const csv = rows.map((r) => r.map(csvCell).join(',')).join('\n');
  const blob = new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function formatDate(iso?: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('es-ES', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

const PERIOD_LABEL: Record<FeePeriod, string> = {
  monthly: 'Mensual',
  quarterly: 'Trimestral',
  annual: 'Anual',
};

function StatusPill({ status }: { status: MemberFeeStatusValue }) {
  const map: Record<MemberFeeStatusValue, { bg: string; label: string; Icon: typeof CheckCircleIcon }> = {
    paid: { bg: 'bg-emerald-100 text-emerald-700', label: 'Cobrado', Icon: CheckCircleIcon },
    pending: { bg: 'bg-amber-100 text-amber-700', label: 'Pendiente', Icon: ClockIcon },
    exempt: { bg: 'bg-gray-200 text-gray-700', label: 'Exento', Icon: MinusCircleIcon },
  };
  const m = map[status];
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${m.bg}`}>
      <m.Icon className="h-3 w-3" />
      {m.label}
    </span>
  );
}

function ModuleDisabled() {
  return (
    <div>
      <Header title="Cuotas" subtitle="Gestión de cuotas de la asociación" />
      <div className="p-6">
        <div className="max-w-xl mx-auto rounded-xl border border-gray-200 bg-white p-8 text-center">
          <NoSymbolIcon className="h-12 w-12 mx-auto text-gray-300" />
          <h2 className="mt-3 text-base font-semibold text-gray-900">
            Módulo Cuotas no habilitado
          </h2>
          <p className="mt-2 text-sm text-gray-500">
            Esta organización no tiene activado el módulo de cuotas. Un administrador puede
            habilitarlo desde la configuración
            (<code className="text-xs">config.management.fees.enabled</code>).
          </p>
        </div>
      </div>
    </div>
  );
}

type Tab = 'statuses' | 'plans' | 'assign' | 'campaigns';

export default function FeesPage() {
  const enabled = useManagementFeature('fees');
  if (!enabled) return <ModuleDisabled />;
  return <FeesInner />;
}

function FeesInner() {
  const { hasPermission } = useAuthStore();
  const canManage = hasPermission(PERMISSIONS.FEES_MANAGE);
  const [tab, setTab] = useState<Tab>('statuses');

  return (
    <div>
      <Header title="Cuotas" subtitle="Planes, cobros y comunicaciones a socios" />

      <div className="p-6 space-y-6">
        <div className="border-b border-gray-200 -mb-px flex items-center gap-4">
          {(['statuses', 'plans', 'assign', 'campaigns'] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`pb-3 px-1 text-sm font-medium border-b-2 ${
                tab === t
                  ? 'border-vida-600 text-vida-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {t === 'statuses' && 'Cobros'}
              {t === 'plans' && 'Planes'}
              {t === 'assign' && 'Asignación'}
              {t === 'campaigns' && 'Campañas'}
            </button>
          ))}
        </div>

        {tab === 'statuses' && <StatusesTab canManage={canManage} />}
        {tab === 'plans' && <PlansTab canManage={canManage} />}
        {tab === 'assign' && <AssignTab canManage={canManage} />}
        {tab === 'campaigns' && <CampaignsTab canManage={canManage} />}
      </div>
    </div>
  );
}

function usePlansAndMembers() {
  const [plans, setPlans] = useState<MemberFee[]>([]);
  const [members, setMembers] = useState<Member[]>([]);

  useEffect(() => {
    feesApi.listPlans().then((r) => setPlans(r.data.fees)).catch(() => {});
    membersApi.list({ limit: 500 }).then((r) => setMembers(r.data.members)).catch(() => {});
  }, []);

  const memberMap = useMemo(() => {
    const m = new Map<string, Member>();
    for (const x of members) m.set(x._id, x);
    return m;
  }, [members]);
  const planMap = useMemo(() => {
    const m = new Map<string, MemberFee>();
    for (const x of plans) m.set(x._id, x);
    return m;
  }, [plans]);

  return { plans, members, memberMap, planMap };
}

// -----------------------------------------------------------------------------
// Tab: Cobros (MemberFeeStatus)
// -----------------------------------------------------------------------------

function StatusesTab({ canManage }: { canManage: boolean }) {
  const { plans, planMap, memberMap } = usePlansAndMembers();
  const [statuses, setStatuses] = useState<MemberFeeStatusRow[]>([]);
  const [statusFilter, setStatusFilter] = useState<MemberFeeStatusValue | 'all'>('all');
  const [planFilter, setPlanFilter] = useState<string>('all');
  const [period, setPeriod] = useState<string>(new Date().getFullYear().toString());
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const params: Parameters<typeof feesApi.listStatuses>[0] = { limit: 500 };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (planFilter !== 'all') params.memberFeeId = planFilter;
      if (period.trim()) params.period = period.trim();
      const { data } = await feesApi.listStatuses(params);
      setStatuses(data.statuses);
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } };
      toast.error(e.response?.data?.error || 'Error cargando cobros');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, planFilter, period]);

  useEffect(() => {
    reload();
  }, [reload]);

  const changeStatus = async (row: MemberFeeStatusRow, next: MemberFeeStatusValue) => {
    if (row.status === next) return;
    try {
      await feesApi.updateStatus(row._id, { status: next });
      toast.success(
        next === 'paid'
          ? 'Cobro marcado como pagado (apunte de presupuesto creado si el plan lo enlaza)'
          : next === 'pending'
            ? 'Cobro marcado como pendiente'
            : 'Cobro marcado como exento'
      );
      reload();
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } };
      toast.error(e.response?.data?.error || 'No se pudo cambiar el estado');
    }
  };

  const removeRow = async (row: MemberFeeStatusRow) => {
    if (!window.confirm('¿Eliminar este cobro? Se borrará también el apunte de presupuesto si lo había.')) return;
    try {
      await feesApi.removeStatus(row._id);
      toast.success('Cobro eliminado');
      reload();
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } };
      toast.error(e.response?.data?.error || 'No se pudo eliminar');
    }
  };

  const stats = useMemo(() => {
    const out = { total: statuses.length, paid: 0, pending: 0, exempt: 0, amountPaid: 0, amountPending: 0 };
    for (const s of statuses) {
      out[s.status]++;
      if (s.status === 'paid') out.amountPaid += s.amount;
      else if (s.status === 'pending') out.amountPending += s.amount;
    }
    return out;
  }, [statuses]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="stat-card"><p className="stat-card-title">Total cobros</p><p className="stat-card-value">{stats.total}</p></div>
        <div className="stat-card"><p className="stat-card-title">Cobrado</p><p className="stat-card-value text-emerald-700">{formatEur2(stats.amountPaid)}</p></div>
        <div className="stat-card"><p className="stat-card-title">Pendiente</p><p className="stat-card-value text-amber-700">{formatEur2(stats.amountPending)}</p></div>
        <div className="stat-card"><p className="stat-card-title">Exentos</p><p className="stat-card-value text-gray-500">{stats.exempt}</p></div>
      </div>

      <div className="flex items-end gap-2 flex-wrap">
        <Field label="Periodo">
          <input
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            placeholder="2026 · 2026-Q1 · 2026-03"
            className="form-input"
          />
        </Field>
        <Field label="Plan">
          <select
            value={planFilter}
            onChange={(e) => setPlanFilter(e.target.value)}
            className="form-input"
          >
            <option value="all">Todos</option>
            {plans.map((p) => (
              <option key={p._id} value={p._id}>
                {p.label}
              </option>
            ))}
          </select>
        </Field>
        <div className="flex items-center gap-1 ml-auto">
          {(['all', 'pending', 'paid', 'exempt'] as const).map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1 text-xs rounded-full border ${
                statusFilter === s
                  ? 'border-vida-600 bg-vida-50 text-vida-700'
                  : 'border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              {s === 'all' ? 'Todos' : s === 'pending' ? 'Pendientes' : s === 'paid' ? 'Cobrados' : 'Exentos'}
            </button>
          ))}
          {statuses.length > 0 && (
            <button
              onClick={() => {
                const rows: string[][] = [
                  ['Periodo', 'Socio', 'Email', 'Plan', 'Importe (EUR)', 'Estado', 'Pagado'],
                  ...statuses.map((s) => {
                    const m = memberMap.get(s.memberId);
                    const p = planMap.get(s.memberFeeId);
                    return [
                      s.period,
                      m?.fullName || '',
                      m?.email || '',
                      p?.label || '',
                      (s.amount / 100).toFixed(2),
                      s.status,
                      s.paidAt ? s.paidAt.slice(0, 10) : '',
                    ];
                  }),
                ];
                downloadCsv(`cuotas-cobros-${period || 'todos'}.csv`, rows);
              }}
              className="ml-2 inline-flex items-center gap-1 px-3 py-1 text-xs border border-gray-300 rounded-full hover:bg-gray-50"
            >
              <ArrowDownTrayIcon className="h-3.5 w-3.5" /> CSV
            </button>
          )}
        </div>
      </div>

      <section className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              <th className="text-left px-4 py-2">Socio</th>
              <th className="text-left px-4 py-2 hidden md:table-cell">Plan</th>
              <th className="text-left px-4 py-2 hidden md:table-cell">Periodo</th>
              <th className="text-right px-4 py-2">Importe</th>
              <th className="text-left px-4 py-2">Estado</th>
              {canManage && <th className="text-right px-4 py-2">Acciones</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading && (
              <tr><td colSpan={canManage ? 6 : 5} className="px-4 py-6 text-center text-sm text-gray-500">Cargando…</td></tr>
            )}
            {!loading && statuses.length === 0 && (
              <tr><td colSpan={canManage ? 6 : 5} className="px-4 py-6 text-center text-sm text-gray-500">Sin cobros para los filtros actuales. Crea cobros desde la pestaña Asignación.</td></tr>
            )}
            {!loading && statuses.map((s) => {
              const member = memberMap.get(s.memberId);
              const plan = planMap.get(s.memberFeeId);
              return (
                <tr key={s._id}>
                  <td className="px-4 py-2">
                    <p className="font-medium text-gray-900">{member?.fullName || s.memberId.slice(0, 8)}</p>
                    {member?.email && <p className="text-xs text-gray-500">{member.email}</p>}
                  </td>
                  <td className="px-4 py-2 text-gray-600 hidden md:table-cell">{plan?.label || '—'}</td>
                  <td className="px-4 py-2 text-gray-600 hidden md:table-cell">{s.period}</td>
                  <td className="px-4 py-2 text-right font-mono text-xs">{formatEur2(s.amount)}</td>
                  <td className="px-4 py-2"><StatusPill status={s.status} /></td>
                  {canManage && (
                    <td className="px-4 py-2 text-right">
                      <div className="inline-flex items-center gap-1">
                        {s.status !== 'paid' && (
                          <button onClick={() => changeStatus(s, 'paid')} title="Marcar como cobrado" className="text-emerald-600 hover:bg-emerald-50 p-1 rounded">
                            <CheckCircleIcon className="h-4 w-4" />
                          </button>
                        )}
                        {s.status !== 'pending' && (
                          <button onClick={() => changeStatus(s, 'pending')} title="Marcar como pendiente" className="text-amber-600 hover:bg-amber-50 p-1 rounded">
                            <ClockIcon className="h-4 w-4" />
                          </button>
                        )}
                        {s.status !== 'exempt' && (
                          <button onClick={() => changeStatus(s, 'exempt')} title="Marcar como exento" className="text-gray-500 hover:bg-gray-100 p-1 rounded">
                            <MinusCircleIcon className="h-4 w-4" />
                          </button>
                        )}
                        <button onClick={() => removeRow(s)} title="Eliminar" className="text-gray-400 hover:text-red-700 p-1 rounded">
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Tab: Planes
// -----------------------------------------------------------------------------

function PlansTab({ canManage }: { canManage: boolean }) {
  const [plans, setPlans] = useState<MemberFee[]>([]);
  const [lines, setLines] = useState<BudgetLine[]>([]);
  const [editing, setEditing] = useState<MemberFee | 'new' | null>(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await feesApi.listPlans();
      setPlans(data.fees);
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } };
      toast.error(e.response?.data?.error || 'Error cargando planes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    let cancelled = false;
    budgetsApi
      .list()
      .then(async ({ data }) => {
        const active = data.budgets.find((b) => b.status === 'approved') || data.budgets[0];
        if (!active) return;
        const res = await budgetsApi.lines(active._id);
        if (!cancelled) setLines(res.data.lines);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const remove = async (p: MemberFee) => {
    if (!window.confirm(`¿Eliminar el plan "${p.label}"?`)) return;
    try {
      await feesApi.removePlan(p._id);
      toast.success('Plan eliminado');
      reload();
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } };
      toast.error(e.response?.data?.error || 'No se pudo eliminar');
    }
  };

  return (
    <div className="space-y-4">
      {canManage && (
        <button
          onClick={() => setEditing('new')}
          className="inline-flex items-center gap-2 px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          <PlusIcon className="h-4 w-4" /> Nuevo plan
        </button>
      )}

      <section className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              <th className="text-left px-4 py-2">Etiqueta</th>
              <th className="text-left px-4 py-2">Periodicidad</th>
              <th className="text-right px-4 py-2">Importe</th>
              <th className="text-left px-4 py-2 hidden md:table-cell">Año</th>
              <th className="text-left px-4 py-2 hidden md:table-cell">Partida ligada</th>
              <th className="text-left px-4 py-2">Activo</th>
              {canManage && <th className="text-right px-4 py-2"></th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading && <tr><td colSpan={canManage ? 7 : 6} className="px-4 py-6 text-center text-sm text-gray-500">Cargando…</td></tr>}
            {!loading && plans.length === 0 && <tr><td colSpan={canManage ? 7 : 6} className="px-4 py-6 text-center text-sm text-gray-500">Aún no hay planes. Crea el primero con "Nuevo plan".</td></tr>}
            {!loading && plans.map((p) => {
              const line = p.budgetLineId ? lines.find((l) => l._id === p.budgetLineId) : null;
              return (
                <tr key={p._id}>
                  <td className="px-4 py-2 font-medium text-gray-900">{p.label}</td>
                  <td className="px-4 py-2 text-gray-600">{PERIOD_LABEL[p.period]}</td>
                  <td className="px-4 py-2 text-right font-mono text-xs">{formatEur2(p.amount)}</td>
                  <td className="px-4 py-2 text-gray-600 hidden md:table-cell">{p.yearScope || '—'}</td>
                  <td className="px-4 py-2 text-gray-600 hidden md:table-cell">{line ? line.label : <span className="text-xs text-gray-400">—</span>}</td>
                  <td className="px-4 py-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${p.active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-200 text-gray-700'}`}>
                      {p.active ? 'Sí' : 'No'}
                    </span>
                  </td>
                  {canManage && (
                    <td className="px-4 py-2 text-right space-x-2">
                      <button onClick={() => setEditing(p)} className="text-gray-400 hover:text-vida-700"><PencilSquareIcon className="h-4 w-4 inline" /></button>
                      <button onClick={() => remove(p)} className="text-gray-400 hover:text-red-700"><TrashIcon className="h-4 w-4 inline" /></button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>

      {editing !== null && (
        <PlanEditor
          initial={editing === 'new' ? null : editing}
          lines={lines}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); }}
        />
      )}
    </div>
  );
}

function PlanEditor({
  initial,
  lines,
  onClose,
  onSaved,
}: {
  initial: MemberFee | null;
  lines: BudgetLine[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = !!initial;
  const [form, setForm] = useState({
    label: initial?.label || '',
    amountEur: initial ? (initial.amount / 100).toString() : '',
    period: initial?.period || ('annual' as FeePeriod),
    yearScope: initial?.yearScope?.toString() || '',
    active: initial?.active ?? true,
    budgetLineId: initial?.budgetLineId || '',
    notes: initial?.notes || '',
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const eur = parseFloat(form.amountEur.replace(',', '.'));
    if (!form.label.trim() || Number.isNaN(eur) || eur < 0) {
      toast.error('Etiqueta y un importe válido son obligatorios');
      return;
    }
    setSaving(true);
    try {
      const payload: Partial<MemberFee> = {
        label: form.label.trim(),
        amount: Math.round(eur * 100),
        period: form.period,
        yearScope: form.yearScope ? parseInt(form.yearScope, 10) : undefined,
        active: form.active,
        budgetLineId: form.budgetLineId || null,
        notes: form.notes || undefined,
      };
      if (isEdit && initial) {
        await feesApi.updatePlan(initial._id, payload);
        toast.success('Plan actualizado');
      } else {
        await feesApi.createPlan(payload);
        toast.success('Plan creado');
      }
      onSaved();
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } };
      toast.error(e.response?.data?.error || 'No se pudo guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <SidePanel title={isEdit ? 'Editar plan' : 'Nuevo plan'} onClose={onClose}>
      <form onSubmit={submit} className="flex flex-col flex-1">
        <div className="p-4 space-y-3 flex-1">
          <Field label="Etiqueta" required>
            <input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="form-input" required />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Importe (€)" required>
              <input type="number" step="0.01" min="0" value={form.amountEur} onChange={(e) => setForm({ ...form, amountEur: e.target.value })} className="form-input" required />
            </Field>
            <Field label="Periodicidad">
              <select value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value as FeePeriod })} className="form-input">
                <option value="annual">Anual</option>
                <option value="quarterly">Trimestral</option>
                <option value="monthly">Mensual</option>
              </select>
            </Field>
          </div>
          <Field label="Año (opcional, si el plan es de un ejercicio concreto)">
            <input type="number" min={2000} max={2200} value={form.yearScope} onChange={(e) => setForm({ ...form, yearScope: e.target.value })} className="form-input" placeholder="2026" />
          </Field>
          <Field label="Partida del presupuesto ligada (opcional)">
            <select value={form.budgetLineId} onChange={(e) => setForm({ ...form, budgetLineId: e.target.value })} className="form-input">
              <option value="">— Sin partida ligada —</option>
              {lines.filter((l) => l.kind === 'income').map((l) => (
                <option key={l._id} value={l._id}>
                  {l.category} · {l.label}
                </option>
              ))}
            </select>
            <p className="mt-1 text-[11px] text-gray-500">
              Cuando un cobro de este plan pasa a "cobrado", se materializa un apunte automáticamente en esta partida.
            </p>
          </Field>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
            Plan activo
          </label>
          <Field label="Notas">
            <textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="form-input" />
          </Field>
        </div>
        <PanelFooter onCancel={onClose} saving={saving} saveLabel={isEdit ? 'Guardar' : 'Crear'} />
      </form>
    </SidePanel>
  );
}

// -----------------------------------------------------------------------------
// Tab: Asignacion
// -----------------------------------------------------------------------------

function AssignTab({ canManage }: { canManage: boolean }) {
  const { plans, members } = usePlansAndMembers();
  const [planId, setPlanId] = useState<string>('');
  const [period, setPeriod] = useState<string>(new Date().getFullYear().toString());
  const [amountEur, setAmountEur] = useState<string>('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState<'all' | 'active' | 'pending'>('active');
  const [submitting, setSubmitting] = useState(false);

  const filteredMembers = useMemo(() => {
    if (filter === 'all') return members;
    return members.filter((m) => m.status === filter);
  }, [members, filter]);

  const toggleAll = () => {
    if (selected.size === filteredMembers.length) setSelected(new Set());
    else setSelected(new Set(filteredMembers.map((m) => m._id)));
  };

  const submit = async () => {
    if (!planId) return toast.error('Selecciona un plan');
    if (!period.trim()) return toast.error('Indica un periodo');
    if (selected.size === 0) return toast.error('Selecciona al menos un socio');
    setSubmitting(true);
    try {
      const payload: Parameters<typeof feesApi.bulkAssign>[0] = {
        memberFeeId: planId,
        period: period.trim(),
        memberIds: Array.from(selected),
      };
      const amt = parseFloat(amountEur.replace(',', '.'));
      if (!Number.isNaN(amt) && amt >= 0) payload.amount = Math.round(amt * 100);
      const { data } = await feesApi.bulkAssign(payload);
      toast.success(
        `${data.inserted} cobro${data.inserted === 1 ? '' : 's'} creados${data.skipped ? ` · ${data.skipped} duplicados omitidos` : ''}`
      );
      setSelected(new Set());
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } };
      toast.error(e.response?.data?.error || 'No se pudo asignar');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Asignar cuota</h3>
        <div className="grid md:grid-cols-3 gap-3">
          <Field label="Plan" required>
            <select value={planId} onChange={(e) => setPlanId(e.target.value)} className="form-input">
              <option value="">— Elige un plan —</option>
              {plans.filter((p) => p.active).map((p) => (
                <option key={p._id} value={p._id}>
                  {p.label} · {formatEur2(p.amount)} ({PERIOD_LABEL[p.period]})
                </option>
              ))}
            </select>
          </Field>
          <Field label="Periodo" required>
            <input value={period} onChange={(e) => setPeriod(e.target.value)} className="form-input" placeholder="2026 · 2026-Q1 · 2026-03" />
          </Field>
          <Field label="Importe (€) — opcional, usa el del plan si vacío">
            <input type="number" step="0.01" min="0" value={amountEur} onChange={(e) => setAmountEur(e.target.value)} className="form-input" />
          </Field>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between p-3 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-gray-700">Socios</span>
            <div className="inline-flex bg-gray-100 rounded-lg p-0.5">
              {(['active', 'all', 'pending'] as const).map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`px-3 py-1 text-xs font-medium rounded-md transition ${
                    filter === f ? 'bg-white shadow text-gray-900' : 'text-gray-600'
                  }`}
                >
                  {f === 'active' ? 'Activos' : f === 'all' ? 'Todos' : 'Pendientes'}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <span className="text-gray-500">{selected.size} seleccionados</span>
            <button onClick={toggleAll} className="text-xs text-vida-700 hover:underline">
              {selected.size === filteredMembers.length ? 'Quitar todos' : 'Seleccionar todos'}
            </button>
          </div>
        </div>
        <ul className="divide-y divide-gray-100 max-h-[450px] overflow-y-auto">
          {filteredMembers.length === 0 && (
            <li className="px-4 py-6 text-center text-sm text-gray-500">No hay socios en esta vista.</li>
          )}
          {filteredMembers.map((m) => {
            const isSel = selected.has(m._id);
            return (
              <li
                key={m._id}
                onClick={() => {
                  const next = new Set(selected);
                  if (isSel) next.delete(m._id);
                  else next.add(m._id);
                  setSelected(next);
                }}
                className={`px-4 py-2 flex items-center gap-3 cursor-pointer ${isSel ? 'bg-vida-50/60' : 'hover:bg-gray-50'}`}
              >
                <input type="checkbox" checked={isSel} readOnly />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{m.fullName}</p>
                  <p className="text-xs text-gray-500 truncate">{m.email || '—'}</p>
                </div>
                <span className="text-xs text-gray-400">{m.status}</span>
              </li>
            );
          })}
        </ul>
      </div>

      {canManage && (
        <div className="flex items-center justify-end">
          <button
            onClick={submit}
            disabled={submitting || selected.size === 0 || !planId}
            className="inline-flex items-center gap-2 px-4 py-2 bg-vida-600 text-white text-sm font-medium rounded-lg hover:bg-vida-700 disabled:opacity-50"
          >
            <ArrowRightIcon className="h-4 w-4" />
            Asignar a {selected.size} socio{selected.size === 1 ? '' : 's'}
          </button>
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Tab: Campanias
// -----------------------------------------------------------------------------

function CampaignsTab({ canManage }: { canManage: boolean }) {
  const { hasPermission } = useAuthStore();
  const canSend = hasPermission(PERMISSIONS.FEES_SEND);
  const [campaigns, setCampaigns] = useState<FeeCampaign[]>([]);
  const [editing, setEditing] = useState<FeeCampaign | 'new' | null>(null);
  const [sendingId, setSendingId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await feesApi.listCampaigns();
      setCampaigns(data.campaigns);
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } };
      toast.error(e.response?.data?.error || 'Error cargando campañas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const remove = async (c: FeeCampaign) => {
    if (!window.confirm(`¿Eliminar la campaña "${c.label}"?`)) return;
    try {
      await feesApi.removeCampaign(c._id);
      toast.success('Campaña eliminada');
      reload();
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } };
      toast.error(e.response?.data?.error || 'No se pudo eliminar');
    }
  };

  const send = async (c: FeeCampaign) => {
    if (c.recipientIds.length === 0) {
      toast.error('La campaña no tiene destinatarios');
      return;
    }
    if (!window.confirm(`¿Enviar la campaña "${c.label}" a ${c.recipientIds.length} destinatarios?`)) return;
    setSendingId(c._id);
    try {
      const { data } = await feesApi.sendCampaign(c._id);
      if (data.failed > 0) {
        toast.error(`${data.sent} enviados · ${data.failed} fallidos`);
      } else {
        toast.success(`Enviado a ${data.sent} destinatarios`);
      }
      reload();
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } };
      toast.error(e.response?.data?.error || 'No se pudo enviar');
    } finally {
      setSendingId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 text-xs text-gray-600">
        El envío usa el servicio de email del módulo C (Resend si está configurado; SMTP si lo está; consola en desarrollo). Los destinatarios se sacan de <code>recipientIds</code> y se filtran por email no vacío.
      </div>

      {canManage && (
        <button onClick={() => setEditing('new')} className="inline-flex items-center gap-2 px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">
          <PlusIcon className="h-4 w-4" /> Nueva campaña
        </button>
      )}

      <section className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              <th className="text-left px-4 py-2">Fecha</th>
              <th className="text-left px-4 py-2">Etiqueta</th>
              <th className="text-left px-4 py-2 hidden md:table-cell">Periodo</th>
              <th className="text-right px-4 py-2">Destinatarios</th>
              <th className="text-left px-4 py-2">Estado</th>
              {canManage && <th className="text-right px-4 py-2"></th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading && <tr><td colSpan={canManage ? 6 : 5} className="px-4 py-6 text-center text-sm text-gray-500">Cargando…</td></tr>}
            {!loading && campaigns.length === 0 && <tr><td colSpan={canManage ? 6 : 5} className="px-4 py-6 text-center text-sm text-gray-500">Sin campañas todavía.</td></tr>}
            {!loading && campaigns.map((c) => (
              <tr key={c._id}>
                <td className="px-4 py-2 text-gray-600">{formatDate(c.created_at)}</td>
                <td className="px-4 py-2 font-medium text-gray-900">{c.label}</td>
                <td className="px-4 py-2 text-gray-600 hidden md:table-cell">{c.period || '—'}</td>
                <td className="px-4 py-2 text-right text-gray-700">{c.recipientIds.length}</td>
                <td className="px-4 py-2">
                  <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">{c.status}</span>
                </td>
                {canManage && (
                  <td className="px-4 py-2 text-right space-x-2">
                    {canSend && c.status !== 'sent' && c.recipientIds.length > 0 && (
                      <button
                        onClick={() => send(c)}
                        disabled={sendingId === c._id}
                        title={`Enviar a ${c.recipientIds.length} destinatarios`}
                        className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-vida-50 text-vida-700 border border-vida-200 rounded-lg hover:bg-vida-100 disabled:opacity-50"
                      >
                        <PaperAirplaneIcon className="h-3.5 w-3.5" />
                        {sendingId === c._id ? 'Enviando…' : 'Enviar'}
                      </button>
                    )}
                    <button onClick={() => setEditing(c)} className="text-gray-400 hover:text-vida-700"><PencilSquareIcon className="h-4 w-4 inline" /></button>
                    <button onClick={() => remove(c)} className="text-gray-400 hover:text-red-700"><TrashIcon className="h-4 w-4 inline" /></button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {editing !== null && (
        <CampaignEditor
          initial={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); }}
        />
      )}
    </div>
  );
}

function CampaignEditor({
  initial,
  onClose,
  onSaved,
}: {
  initial: FeeCampaign | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = !!initial;
  const { plans, members } = usePlansAndMembers();
  const [form, setForm] = useState({
    label: initial?.label || '',
    period: initial?.period || '',
    notes: initial?.notes || '',
    memberFeeId: initial?.memberFeeId || '',
  });
  const [recipients, setRecipients] = useState<Set<string>>(
    new Set(initial?.recipientIds || [])
  );
  const [recipientFilter, setRecipientFilter] = useState<'active' | 'all' | 'pending'>('active');
  const [saving, setSaving] = useState(false);

  const filteredMembers = useMemo(() => {
    if (recipientFilter === 'all') return members.filter((m) => m.email);
    return members.filter((m) => m.email && m.status === recipientFilter);
  }, [members, recipientFilter]);

  const toggleAll = () => {
    if (recipients.size === filteredMembers.length) {
      setRecipients(new Set());
    } else {
      setRecipients(new Set(filteredMembers.map((m) => m._id)));
    }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.label.trim()) return toast.error('La etiqueta es obligatoria');
    setSaving(true);
    try {
      const payload: Partial<FeeCampaign> = {
        label: form.label.trim(),
        period: form.period || undefined,
        notes: form.notes || undefined,
        memberFeeId: form.memberFeeId || null,
        recipientIds: Array.from(recipients),
      };
      if (isEdit && initial) {
        await feesApi.updateCampaign(initial._id, payload);
        toast.success('Campaña actualizada');
      } else {
        await feesApi.createCampaign(payload);
        toast.success('Campaña creada');
      }
      onSaved();
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } };
      toast.error(e.response?.data?.error || 'No se pudo guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <SidePanel title={isEdit ? 'Editar campaña' : 'Nueva campaña'} onClose={onClose}>
      <form onSubmit={submit} className="flex flex-col flex-1">
        <div className="p-4 space-y-3 flex-1">
          <Field label="Etiqueta" required>
            <input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="form-input" required />
          </Field>
          <Field label="Periodo">
            <input value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} className="form-input" placeholder="2026 · 2026-Q1 · 2026-03" />
          </Field>
          <Field label="Plan (opcional — se incluye en el email)">
            <select
              value={form.memberFeeId || ''}
              onChange={(e) => setForm({ ...form, memberFeeId: e.target.value })}
              className="form-input"
            >
              <option value="">— Sin plan asociado —</option>
              {plans.map((p) => (
                <option key={p._id} value={p._id}>
                  {p.label} · {formatEur2(p.amount)}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Notas (aparecen en el cuerpo del email)">
            <textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="form-input" />
          </Field>

          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-gray-700">
                Destinatarios <span className="text-gray-500">({recipients.size} seleccionados)</span>
              </span>
              <div className="inline-flex bg-gray-100 rounded-lg p-0.5">
                {(['active', 'all', 'pending'] as const).map((f) => (
                  <button
                    key={f}
                    type="button"
                    onClick={() => setRecipientFilter(f)}
                    className={`px-2 py-0.5 text-[10px] font-medium rounded transition ${
                      recipientFilter === f ? 'bg-white shadow text-gray-900' : 'text-gray-600'
                    }`}
                  >
                    {f === 'active' ? 'Activos' : f === 'all' ? 'Todos' : 'Pendientes'}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-end mb-1.5">
              <button
                type="button"
                onClick={toggleAll}
                className="text-[11px] text-vida-700 hover:underline"
              >
                {recipients.size === filteredMembers.length ? 'Quitar todos' : 'Seleccionar todos'}
              </button>
            </div>
            <ul className="border border-gray-200 rounded-lg max-h-44 overflow-y-auto divide-y divide-gray-100">
              {filteredMembers.length === 0 && (
                <li className="px-2 py-3 text-center text-xs text-gray-500">
                  Sin socios con email en esta vista.
                </li>
              )}
              {filteredMembers.map((m) => {
                const isSel = recipients.has(m._id);
                return (
                  <li
                    key={m._id}
                    onClick={() => {
                      const next = new Set(recipients);
                      if (isSel) next.delete(m._id);
                      else next.add(m._id);
                      setRecipients(next);
                    }}
                    className={`px-2 py-1.5 text-xs flex items-center gap-2 cursor-pointer ${
                      isSel ? 'bg-vida-50/60' : 'hover:bg-gray-50'
                    }`}
                  >
                    <input type="checkbox" checked={isSel} readOnly />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">{m.fullName}</p>
                      <p className="text-gray-500 truncate">{m.email}</p>
                    </div>
                  </li>
                );
              })}
            </ul>
            <p className="mt-1 text-[10px] text-gray-500">
              Solo aparecen los socios que tienen email registrado.
            </p>
          </div>

          <p className="text-xs text-gray-500 inline-flex items-center gap-1">
            <PaperAirplaneIcon className="h-3.5 w-3.5" />
            Tras guardar, usa el botón "Enviar" en la lista. Se utilizará Resend si la org lo tiene configurado; si no, SMTP; si no, se imprimirá en la consola del backend (desarrollo).
          </p>
        </div>
        <PanelFooter onCancel={onClose} saving={saving} saveLabel={isEdit ? 'Guardar' : 'Crear'} />
      </form>
    </SidePanel>
  );
}

// -----------------------------------------------------------------------------
// Side panel + bits
// -----------------------------------------------------------------------------

function SidePanel({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex" onClick={onClose}>
      <div className="flex-1 bg-black/30" />
      <div className="w-full max-w-md bg-white shadow-xl overflow-auto flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-base font-semibold text-gray-900">{title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function PanelFooter({
  onCancel,
  saving,
  saveLabel,
}: {
  onCancel: () => void;
  saving: boolean;
  saveLabel: string;
}) {
  return (
    <div className="p-4 border-t border-gray-100 flex items-center justify-end gap-2">
      <button type="button" onClick={onCancel} className="px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 rounded-lg">Cancelar</button>
      <button type="submit" disabled={saving} className="px-4 py-1.5 text-sm bg-vida-600 text-white rounded-lg hover:bg-vida-700 disabled:opacity-50">
        {saving ? 'Guardando…' : saveLabel}
      </button>
    </div>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-gray-700">
        {label}
        {required && <span className="text-red-600 ml-0.5">*</span>}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
